import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { AuthState, AuthUser, LoginResponse, Role } from '@/types/auth';
import { decodeJwt, isJwtExpired } from '@/lib/jwt';

interface AuthStore extends AuthState {
  setSession: (response: LoginResponse) => void;
  clear: () => void;
  isAuthenticated: () => boolean;
  hasRole: (role: Role) => boolean;
}

const EMPTY: AuthState = {
  token: null,
  user: null,
  expiresAt: null,
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...EMPTY,

      setSession: (response) => {
        const payload = decodeJwt(response.token);
        if (!payload) {
          set(EMPTY);
          return;
        }
        const user: AuthUser = {
          id: response.id,
          email: response.email,
          role: payload.role,
        };
        set({
          token: response.token,
          user,
          expiresAt: payload.exp * 1000,
        });
      },

      clear: () => set(EMPTY),

      isAuthenticated: () => {
        const { token, expiresAt } = get();
        if (!token) return false;
        if (expiresAt && expiresAt <= Date.now()) return false;
        const payload = decodeJwt(token);
        return !isJwtExpired(payload);
      },

      hasRole: (role) => {
        const { user } = get();
        return user?.role === role;
      },
    }),
    {
      name: 'admin-dh3g-auth-v1',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        expiresAt: state.expiresAt,
      }),
    },
  ),
);

export const getAuthToken = (): string | null => useAuthStore.getState().token;
export const clearAuth = (): void => useAuthStore.getState().clear();
